import { useCallback, useEffect, useState } from 'react';
import { getTokuPaymentList, getMercadopagoPaymentList } from '../services/payment';
import codes from '../utils/providerCodes.json';
import { message } from 'antd';

const usePaymentAmount = (reservation) => {
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPaymentAmount = useCallback(async () => {
    if (!reservation?.number || !reservation?.payment_methods_codes) {
      return;
    }

    const hasToku = reservation.payment_methods_codes.includes(codes.toku);
    const hasMercadopago = reservation.payment_methods_codes.includes(codes.mercadopagocl);

    if (!hasToku && !hasMercadopago) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const params = { reservationNumber: reservation.number };
      let totalPaidAmount = 0;
      let paymentDetails = {};

      if (hasToku) {
        try {
          const tokuResponse = await getTokuPaymentList(params);
          if (tokuResponse && tokuResponse.length > 0) {
            const tokuData = tokuResponse.find(r => r.reservation_number === reservation.number);
            if (tokuData) {
              const total_paid_amount = (tokuData.payments || [])
                .filter(payment => payment.status === 'SUCCESS')
                .reduce((sum, payment) => sum + (payment.paid_amount || 0), 0);

              totalPaidAmount += total_paid_amount;
              paymentDetails.toku = {
                total_paid_amount,
                payments: tokuData.payments || [],
                invoices: tokuData.invoices || []
              };
            }
          }
        } catch (err) {
          console.error('Error fetching Toku payment:', err);
          message.error({
            content: 'Error al obtener el pago de Toku',
            style: { marginTop: '45vh' },
          });
        }
      }

      if (hasMercadopago) {
        try {
          const mercadopagoResponse = await getMercadopagoPaymentList(params);
          if (mercadopagoResponse && mercadopagoResponse.length > 0) {
            const mercadopagoData = mercadopagoResponse.find(r => r.reservation_number === reservation.number);
            if (mercadopagoData) {
              const total_paid_amount = (mercadopagoData.payments || [])
                .filter(payment => payment.status === 'approved')
                .reduce((sum, payment) => sum + (payment.paid_amount || 0), 0);

              totalPaidAmount += total_paid_amount;
              paymentDetails.mercadopago = {
                total_paid_amount,
                payments: mercadopagoData.payments || [],
                invoices: mercadopagoData.invoices || []
              };
            }
          }
        } catch (err) {
          console.error('Error fetching Mercado Pago payment:', err);
          message.error({
            content: 'Error al obtener el pago de Mercado Pago',
            style: { marginTop: '45vh' },
          });
        }
      }

      setPaymentAmount({
        total: totalPaidAmount,
        details: paymentDetails
      });

    } catch (err) {
      console.error('Error fetching payment amount:', err);
      message.error({
        content: 'Error al obtener el monto pagado',
        style: { marginTop: '45vh' },
      });
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [reservation?.number, reservation?.payment_methods_codes]);

  useEffect(() => {
    fetchPaymentAmount();
  }, [fetchPaymentAmount]);

  return {
    paymentAmount,
    isLoading,
    error,
    refetch: fetchPaymentAmount
  };
};

export default usePaymentAmount;
